import type {
  QuestionResponse,
  RecordResponseRequest,
  RenderableQuestion,
  RenderableQuestionType,
  SectionViewQuestion,
} from '@assessments/shared'
import { isAnswered } from '../response-autosave/index.js'

// Bridges the EA-60 section-view payload (SectionViewQuestion, keyed by answerKind +
// a raw display value) onto the EA-67 renderer's contract (RenderableQuestion +
// discriminated QuestionResponse), so the wired section view renders every question
// with the type-appropriate input and persists edits (EA-67). `file`/`unsupported`
// map to the renderer's read-only `unknown` branch, preserving the stored value.

const TYPE_BY_ANSWER_KIND: Record<SectionViewQuestion['answerKind'], RenderableQuestionType> = {
  single_select: 'single_select',
  multi_select: 'multi_select',
  write_in: 'write_in',
  essay: 'essay',
  industry: 'industry',
  file: 'unknown',
  unsupported: 'unknown',
}

/** `label` MUST be the prompt — it drives the input's accessible name. */
export function toRenderableQuestion(question: SectionViewQuestion): RenderableQuestion {
  return {
    id: question.id,
    type: TYPE_BY_ANSWER_KIND[question.answerKind],
    label: question.prompt,
    helperText: question.helperText,
    required: question.required,
    sort: question.sort,
    sectionId: '',
    options: [...question.options]
      .sort((a, b) => a.sort - b.sort)
      .map((o) => ({ id: o.id, label: o.label, isExclusive: o.isExclusive })),
    ...(question.industry ? { industry: question.industry } : {}),
  }
}

/**
 * Maps the renderer's discriminated `QuestionResponse` onto the autosave endpoint's
 * flat `RecordResponseRequest` (EA-63): free text → `response`, selections →
 * `selectedOptionIds`. A cleared answer maps to an empty payload — autosave persists
 * it without validation (validation is the Mark Complete gate's concern, PRD §8.2).
 */
export function toRecordRequest(response: QuestionResponse): RecordResponseRequest {
  switch (response.kind) {
    case 'single':
      return {
        response: null,
        selectedOptionIds: response.selectedOptionId ? [response.selectedOptionId] : [],
      }
    case 'multi':
      return { response: null, selectedOptionIds: response.selectedOptionIds }
    case 'text':
      return { response: response.text, selectedOptionIds: [] }
    case 'industry':
      return { selectedOptionIds: [], subIndustryId: response.subIndustryId }
  }
}

/**
 * Seeds the initial response from the saved answer. Reads `answer.value` regardless
 * of `source` so a prefilled stub still shows its imported value (FR-014).
 */
export function toInitialResponse(question: SectionViewQuestion): QuestionResponse {
  const value = question.answer?.value ?? null
  switch (question.answerKind) {
    case 'single_select':
      return { kind: 'single', selectedOptionId: value }
    case 'multi_select':
      return { kind: 'multi', selectedOptionIds: value ? value.split(',') : [] }
    case 'industry':
      return { kind: 'industry', subIndustryId: question.industry?.saved?.subIndustryId ?? null }
    default:
      return { kind: 'text', text: value ?? '' }
  }
}

/**
 * The answer writes a section exit must persist (Mark complete and Done editing share
 * this): every answered question plus any edited-then-cleared one (so the clear
 * overwrites the stored value), skipping untouched-empty questions (a write would
 * create a non-answer that counts toward progress) and answers autosave already
 * stored unchanged. An industry answer is written only once the respondent changes it:
 * re-sending an untouched one adds nothing, and one whose industry has since been retired
 * would be refused.
 */
export function pendingAnswerWrites(
  responses: Record<string, QuestionResponse>,
  dirtyIds: ReadonlySet<string>,
  isClean: (questionId: string) => boolean,
): Array<readonly [string, RecordResponseRequest]> {
  return Object.entries(responses)
    .filter(([id, response]) => response.kind !== 'industry' || dirtyIds.has(id))
    .map(([id, response]) => [id, toRecordRequest(response)] as const)
    .filter(([id, request]) => !isClean(id) && (isAnswered(request) || dirtyIds.has(id)))
}
